import { useState } from 'react'
import { api, errorText } from '../api/client'
import type { ContactRole, RolePermission } from '../api/types'
import { Icon } from './Icon'
import { ErrorState, Loading } from './States'
import { useToast } from './Toast'
import { useAsync } from '../lib/useAsync'

/** What each permission does, in the words an officer would use. The column
 *  headers are short; these are the tooltips, because "campaigns: off" is only
 *  obvious once you know it is the thing keeping a title closer out of a drip. */
const PERMISSIONS: { key: RolePermission; head: string; help: string }[] = [
  { key: 'campaigns', head: 'Campaigns', help: 'A drip or workflow may select them, and the Workflows section shows on their card. Off means no campaign can reach them, however well they match its filters.' },
  { key: 'partners', head: 'Partners', help: 'Counts as a referral partner — the Partners page and the “gone quiet” alert on Today.' },
  { key: 'intake', head: 'Loan goal', help: 'Shows the mortgage questions: purpose, timeline, price, credit band.' },
  { key: 'loan_party', head: 'Loans', help: 'Expected to turn up on a loan file, so their card lists the files they are on.' },
]

/** The role vocabulary, and what each role is allowed to do.
 *
 *  Branch-wide rather than personal, and admin-only: this is the switch that
 *  decides whether a whole category of people can be marketed to, which is not
 *  a per-officer preference. */
export function ContactRoles({ isAdmin }: { isAdmin: boolean }) {
  const { data, error, loading, reload } = useAsync(() => api.contactRoles(), [])
  const [busy, setBusy] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const toast = useToast()

  if (loading && !data) return <Loading />
  if (error || !data) return <ErrorState error={error} onRetry={reload} />

  const toggle = async (role: ContactRole, key: RolePermission) => {
    setBusy(role.key)
    try {
      await api.editContactRole(role.key, { [key]: !role[key] })
      reload()
    } catch (e) {
      toast(errorText(e), { tone: 'warning' })
    } finally {
      setBusy(null)
    }
  }

  const add = async () => {
    const label = name.trim()
    if (!label) return
    setBusy('new')
    try {
      await api.addContactRole({ label })
      setName('')
      setAdding(false)
      toast(`Added “${label}”. It starts permissive — turn off what shouldn’t apply.`)
      reload()
    } catch (e) {
      toast(errorText(e), { tone: 'warning' })
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="h2">What a contact can be</h2>
        <span className="spacer" />
        {isAdmin ? (
          <button className="btn sm" onClick={() => setAdding(true)} disabled={adding}>
            <Icon name="add" size={15} /> Add a role
          </button>
        ) : null}
      </div>
      <div className="card-body" style={{ paddingTop: 0 }}>
        <p className="quiet" style={{ marginTop: 0, fontSize: 12.5 }}>
          Each role decides what the CRM does with that kind of contact.
          {' '}<strong>{data.unset}</strong> contacts have no role set and behave as they always
          have — nothing changes for them until you give them one.
          {isAdmin ? null : ' Only an admin can change these.'}
        </p>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Role</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500, color: 'var(--ink-3)', fontSize: 11.5 }}>Kind</th>
                <th title="A role either has a lifecycle or it does not. Only Client does — a title company has no funnel position."
                  style={{ padding: '6px 8px', fontWeight: 500, fontSize: 11.5, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                  Statuses
                </th>
                {PERMISSIONS.map((p) => (
                  <th key={p.key} title={p.help}
                    style={{ padding: '6px 8px', fontWeight: 500, fontSize: 11.5, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                    {p.head}
                  </th>
                ))}
                <th style={{ padding: '6px 8px', fontWeight: 500, fontSize: 11.5, color: 'var(--ink-3)', textAlign: 'right' }}>
                  Contacts
                </th>
              </tr>
            </thead>
            <tbody>
              {data.roles.map((r) => (
                <tr key={r.key} style={{ borderTop: '1px solid var(--line-faint)', opacity: busy === r.key ? 0.5 : 1 }}>
                  <td style={{ padding: '7px 8px', fontWeight: 500 }}>
                    {r.entity_kind === 'company'
                      ? <Icon name="domain" size={14} style={{ verticalAlign: -2, color: 'var(--ink-3)' }} />
                      : null}{' '}{r.label}
                  </td>
                  <td className="quiet" style={{ padding: '7px 8px', fontSize: 11.5 }}>{r.entity_kind}</td>
                  <td className="quiet" style={{ padding: '7px 8px', fontSize: 11.5, whiteSpace: 'nowrap' }}>
                    {(data.statuses[r.key] ?? []).length
                      ? `${data.statuses[r.key]!.length} stages`
                      : <span style={{ opacity: 0.6 }}>none</span>}
                  </td>
                  {PERMISSIONS.map((p) => (
                    <td key={p.key} style={{ padding: '7px 8px', textAlign: 'center' }}>
                      <button
                        className={`toggle ${r[p.key] ? 'on' : 'off'}`}
                        disabled={!isAdmin || busy !== null}
                        aria-pressed={r[p.key]}
                        aria-label={`${p.head} for ${r.label}`}
                        title={p.help}
                        onClick={() => toggle(r, p.key)}>
                        <span className="knob" />
                      </button>
                    </td>
                  ))}
                  <td className="mono quiet" style={{ padding: '7px 8px', textAlign: 'right', fontSize: 12 }}>
                    {r.contacts || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {adding ? (
          <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
            <input className="input" style={{ maxWidth: 260 }} autoFocus value={name}
              placeholder="Escrow assistant" onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') add() }} />
            <button className="btn primary" onClick={add} disabled={!name.trim() || busy !== null}>Add</button>
            <button className="btn" onClick={() => { setAdding(false); setName('') }}>Cancel</button>
            <span className="quiet" style={{ fontSize: 11.5 }}>
              A new role starts able to do everything a lead can — turn off what shouldn’t apply.
            </span>
          </div>
        ) : null}

        <p className="quiet" style={{ fontSize: 11.5, marginBottom: 0, marginTop: 14 }}>
          <Icon name="info" size={13} style={{ verticalAlign: -2 }} /> Turning
          {' '}<strong>Campaigns</strong> off is what keeps a role out of every drip audience. It
          does not replace the consent rules — nobody is texted or emailed without opt-in either way.
        </p>
      </div>
    </section>
  )
}
